import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import _difference from 'lodash/difference';
import _isEqual from 'lodash/isEqual';
import _keyBy from 'lodash/keyBy';
import { regionsGrouped, countries } from 'constants/regions';
import { InputDropdown, MultiLevelDropdown } from 'bonfire-kindling';

const countriesIndexed = _keyBy(countries, 'value');

const getFilteredTree = (treeData, available) => {
  if (!available || !available.length) {
    return treeData;
  }
  const availableIndexed = _keyBy(available);
  const newTree = treeData.reduce((countries, country) => {
    const availableChildren = country.children.filter(({ value }) => availableIndexed[value]);
    if (availableChildren.length) {
      countries.push({ ...country, children: availableChildren });
    }
    return countries;
  }, []);
  return newTree;
};

const getCheckedLabels = (treeData, checked) =>
  treeData.reduce((values, parent) => {
    const children = parent.children.map(child => child.value);
    const allChildrenChecked = _difference(children, checked).length === 0;
    if (allChildrenChecked) {
      values.push(parent.label);
    } else {
      values.push(
        ...parent.children.filter(child => checked.includes(child.value)).map(child => child.label)
      );
    }

    return values;
  }, []);

const FilterByLocation = ({ onChange, values, availableValues, label, TextFieldProps }) => {
  const { t } = useTranslation();
  const treeData = useMemo(
    () =>
      Object.entries(regionsGrouped).map(([key, subregions]) => ({
        // Duplicate child values; CA/US are both key and first subregion, due to "nation-wide opportunities"
        // React-checkbox-tree doesn't like that, so using the full country name instead for parent value
        value: countriesIndexed[key]?.label || key,
        label: countriesIndexed[key]?.label || t('opportunities.filters.other_regions'),
        children: subregions.map(({ value, label }) => ({
          value,
          label,
        })),
      })),
    [t]
  );
  const availableTree = useMemo(
    () => getFilteredTree(treeData, availableValues),
    [treeData, availableValues]
  );
  const checkedLabels = getCheckedLabels(treeData, values);
  const inputValueFormatted = checkedLabels.join(', ');

  return (
    <InputDropdown
      aria-label={t('opportunities.filters.location_filter')}
      label={label}
      value={inputValueFormatted}
      TextFieldProps={{
        placeholder: t('opportunities.filters.location_placeholder'),
        InputProps: {
          inputProps: {
            sx: {
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            'data-cy': 'locationFilter',
          },
        },
        ...TextFieldProps,
      }}
    >
      <MultiLevelDropdown
        initialChecked={availableValues}
        checked={values}
        nodes={availableTree}
        noResultsLabel={t('opportunities.filters.no_locations')}
        disableReset
        onCheck={checked => {
          if (!_isEqual(checked, values)) {
            onChange(checked);
          }
        }}
      />
    </InputDropdown>
  );
};

export default FilterByLocation;
